import axios from 'axios';
import { ENV_CONFIG } from '@/types/environment';
import { TokenManager } from '@/utils/tokenManager';
import { AUTH_CONSTANTS } from '@/constants/auth';
import { getCSRFToken } from '@/utils/csrf';
import { getClientLoggingOut } from '@/stores/useAuthStore';
import { trackApiCall } from '@/utils/sentry';
// MonitoringOverview removed - using only Sentry tracking

// API Base Configuration
const API_BASE_URL = ENV_CONFIG.API_URL;

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: ENV_CONFIG.API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication and performance tracking
apiClient.interceptors.request.use(
  (config) => {
    // Performance-Tracking: Start-Zeit setzen
    (config as any).metadata = { startTime: performance.now() };
    
    // Skip authentication wenn logout läuft
    if (getClientLoggingOut()) {
      return config;
    }
    
    // Add JWT token if available
    const token = TokenManager.getToken(AUTH_CONSTANTS.TOKEN_KEYS.ACCESS);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add API token if available (for DRF Token Authentication)
    const apiToken = TokenManager.getToken(AUTH_CONSTANTS.TOKEN_KEYS.API);
    if (apiToken) {
      config.headers.Authorization = `Token ${apiToken}`;
    }
    
    // Add CSRF token for state-changing requests (POST, PUT, DELETE, PATCH)
    const method = config.method?.toUpperCase();
    if (method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
        console.log('🔒 CSRF-Token hinzugefügt für', method, 'Request');
      } else {
        console.warn('⚠️ Kein CSRF-Token verfügbar für', method, 'Request');
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and performance tracking
apiClient.interceptors.response.use(
  (response) => {
    // Performance-Tracking: End-Zeit setzen
    (response.config as any).metadata = { 
      ...(response.config as any).metadata, 
      endTime: performance.now() 
    };
    
    // Performance-Tracking für erfolgreiche Requests
    const duration = (response.config as any).metadata?.endTime - (response.config as any).metadata?.startTime;
    if (duration) {
      // Sentry-Tracking
      trackApiCall(
        response.config.url || 'unknown',
        response.config.method?.toUpperCase() || 'GET',
        duration,
        response.status
      );
      // Local performance tracking removed - using only Sentry
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Performance-Tracking für fehlgeschlagene Requests
    const duration = (originalRequest as any)?.metadata?.endTime - (originalRequest as any)?.metadata?.startTime;
    if (duration) {
      // Sentry-Tracking
      trackApiCall(
        originalRequest?.url || 'unknown',
        originalRequest?.method?.toUpperCase() || 'GET',
        duration,
        error.response?.status || 0
      );
      // Local performance tracking removed - using only Sentry
    }
    
    // NIEMALS Token refresh versuchen wenn logout läuft!
    if (getClientLoggingOut()) {
      return Promise.reject(error);
    }
    
    // Handle 401 errors (unauthorized) - NUR wenn nicht bereits ein Retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Prüfe ob User noch authentifiziert sein sollte
      const hasValidTokens = TokenManager.hasAnyToken();
      
      if (!hasValidTokens) {
        console.log('No valid tokens found, not attempting refresh');
        return Promise.reject(error);
      }
      
      // Try to refresh token
      const refreshToken = TokenManager.getToken(AUTH_CONSTANTS.TOKEN_KEYS.REFRESH);
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/token/refresh/`, {
            refresh: refreshToken,
          });
          
          const { access } = response.data;
          TokenManager.setToken(AUTH_CONSTANTS.TOKEN_KEYS.ACCESS, access);
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return apiClient(originalRequest);
        } catch {
          // Refresh failed, trigger logout durch Event
          console.log('Token refresh failed, triggering logout');
          window.dispatchEvent(new CustomEvent(AUTH_CONSTANTS.EVENTS.LOGOUT_REQUIRED));
          return Promise.reject(error);
        }
      } else {
        // No refresh token, trigger logout durch Event
        console.log('No refresh token, triggering logout');
        window.dispatchEvent(new CustomEvent(AUTH_CONSTANTS.EVENTS.LOGOUT_REQUIRED));
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

// Export function to set logout state
// Helper function to set logout state - now handled by useAuthStore
export const setClientLoggingOut = (_value: boolean) => {
  // This function is now handled by useAuthStore
  // Keeping for backward compatibility
  console.warn('setClientLoggingOut is deprecated, use useAuthStore instead');
};

export default apiClient;
